import { useEffect, useMemo } from 'react';
import { PackageType, useFacetsQuery } from '@gql/schema';
import { buildGroupsAndPeopleFromSelection } from '@secretescapes/cauldron';
import { useAppState } from '@utils/useAppState';
import { useApplicationState } from '@utils/useApplicationState';
import { useTranslations } from '@utils/useTranslations';
import { type IFlightState } from '@features/Flights';
import { ALL_OFFERS, getAirportOptions, getNightOptions } from './AvailabilityPresentation.utils';
import { getFacetOffers } from './Offers/Offers.utils';
import { useSelectionActions, useSelectionState } from './SelectionContext';

interface Props {
  flightsSelected: boolean;
  flightState: IFlightState;
}

export const useFacets = ({ flightsSelected, flightState }: Props) => {
  const { saleId } = useAppState();
  const { affiliateCode, territoryCode, isMobile } = useApplicationState();
  const { translations } = useTranslations();
  const { offerId, travellersSelection } = useSelectionState();
  const { changeOfferId } = useSelectionActions();

  const { people, groups } = buildGroupsAndPeopleFromSelection(travellersSelection);
  const { selectedAirport, selectedNights } = flightState;

  const { data, previousData, error, loading, refetch } = useFacetsQuery({
    variables: {
      saleId,
      affiliate: affiliateCode,
      territory: territoryCode,
      packageGroups: [],
      people,
      groups,
      nights: flightsSelected && selectedNights ? [selectedNights] : [],
      departureAirports: flightsSelected && selectedAirport.code ? [selectedAirport.code] : [],
      packageType: flightsSelected ? PackageType.IncludingFlights : PackageType.Hotel,
    },
    fetchPolicy: 'no-cache',
  });

  useEffect(() => {
    // Reset offer id when current offer id is not available with flights selection
    if (data && isMobile && offerId !== ALL_OFFERS) {
      const selectedOfferExists = data.sale?.calendarFacets?.offers?.find(o => o.id === offerId);
      if (!selectedOfferExists) {
        changeOfferId(ALL_OFFERS);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const { airports, nights, offers } = useMemo(() => {
    const dataToUse = loading ? previousData : data;

    return {
      airports: getAirportOptions(dataToUse?.sale?.calendarFacets?.airports, translations),
      nights: getNightOptions(dataToUse?.sale?.calendarFacets?.nights, translations),
      offers: getFacetOffers(dataToUse?.sale?.calendarFacets),
    };
  }, [loading, data, previousData, translations]);

  return {
    airports,
    nights,
    facetsOffers: offers,
    facetsError: !!error,
    facetsLoading: loading,
    facetsRefetch: refetch,
  };
};
